import type React from "react";
import { useId } from "react";
import { cn } from "@/lib/utils";

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /** Classes for the bubble itself -- override when content needs to wrap
   * (the default is a short one-line label) or needs a wider box. */
  contentClassName?: string;
  side?: "top" | "bottom";
}

// Reveals on hover *and* keyboard focus (previously hover-only, so keyboard
// and touch users had no way to see this at all -- UI-18), and associates
// the bubble with its trigger via aria-describedby so assistive tech
// announces it as a description, not just floating unrelated text. The
// wrapper itself is the focusable element (tabIndex here) rather than
// requiring a focusable child, since most callers wrap a plain icon or span.
export function Tooltip({
  content,
  children,
  className,
  contentClassName,
  side = "top",
}: TooltipProps): React.JSX.Element {
  const tooltipId = useId();
  const above = side === "top";
  return (
    <div
      tabIndex={0}
      aria-describedby={tooltipId}
      className={cn(
        "relative inline-flex group rounded-sm",
        "focus:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        className,
      )}
    >
      {children}
      <div
        id={tooltipId}
        role="tooltip"
        className={cn(
          "absolute left-1/2 -translate-x-1/2 px-2.5 py-1.5 rounded-md",
          "bg-foreground text-background text-xs whitespace-nowrap",
          "pointer-events-none opacity-0 transition-opacity z-50",
          "group-hover:opacity-100 group-focus:opacity-100 group-focus-within:opacity-100",
          above ? "bottom-full mb-2" : "top-full mt-2",
          contentClassName,
        )}
      >
        {content}
        <div
          className={cn(
            "absolute left-1/2 -translate-x-1/2 border-4 border-transparent",
            above
              ? "top-full -mt-px border-t-foreground"
              : "bottom-full -mb-px border-b-foreground",
          )}
        />
      </div>
    </div>
  );
}
